import React, { useEffect, useState } from "react";
import Menubar from "../Menubar";
import { store } from "../../App";
import { Col, Container, Row } from "react-bootstrap";
import KpiService from "../../Services/KpiService";
import { Lottie } from "lottie-react";
import ratingAnimation from "../../assets/rating.json";
import { AiFillEdit, AiOutlineSearch } from "react-icons/ai";
import { FaDownload, FaEdit, FaEye, FaFileExcel, FaUsers } from "react-icons/fa";
import { downloadpdf } from "../utils/commonFunctions";
import FeedbackBookViewer from "./FeedbackBookViewer";
import { CiEdit } from "react-icons/ci";
import * as XLSX from "xlsx-js-style";
import { FcRating } from "react-icons/fc";
import { MdRateReview, MdReviews } from "react-icons/md";
import { TbPencilStar } from "react-icons/tb";

export default function EvaluationForm() {
    const userid = localStorage.getItem("username");
    const deptid = localStorage.getItem("deptid");
    const roleid = localStorage.getItem("role");
    const districtid = localStorage.getItem("districtid");

    const ratings = ["Excellent", "Good", "Average", "Poor"];

    const levels = [
        { id: "1", name: "Secretariat Officers", ids: ["1"] },
        { id: "2", name: "HoD Officers", ids: ["2"] },
        { id: "3", name: "District Officers", ids: ["3", "4", "5", "6", "7"] },
    ];

    const [data, setData] = useState({
        evaluationRows: [],
        ratingRows: [],
        departmentRows: [],
        districtRows: [],
        themeRows: [],
    });

    const [allRecords, setAllRecords] = useState([]);
    const [apiMessage, setApiMessage] = useState("");
    const [details, setDetails] = useState({ title: "", records: [] });
    const [detailSearch, setDetailSearch] = useState("");
    const [refreshDashboard, setRefreshDashboard] = useState(0);

    const [tableSearch, setTableSearch] = useState({
        evaluation: "",
        rating: "",
        department: "",
        district: "",
        themeRows: "",
        details: "",

    });

    const [tableSort, setTableSort] = useState({});

    const normalize = (value) =>
        String(value ?? "").trim().toLowerCase();

    const levelId = (value) => {
        const s = normalize(value);

        return (
            levels.find(
                (level) =>
                    level.ids.includes(s) ||
                    normalize(level.name.replace(" Officers", "")) === s ||
                    normalize(level.name) === s
            )?.id || ""
        );
    };

    const isSubmitted = (record) =>
        [true, 1, "1", "true"].includes(
            typeof record?.is_finalsubmit === "string"
                ? record.is_finalsubmit.trim().toLowerCase()
                : record?.is_finalsubmit
        );

    const isEvaluated = (record) =>
        record?.eval_grade != null &&
        String(record.eval_grade).trim() !== "";

    const gradeMatches = (record, rating) =>
        normalize(record?.eval_grade) === normalize(rating);

    useEffect(() => {


        const params = {
            mode: "dashboarddata",
            userId: userid,
            districtId: ["2", "64", "3"].includes(String(roleid))
                ? districtid
                : "0",
            deptId: String(roleid) === "8" ? "0" : deptid,
        };

        store.dispatch({ type: "SHOW_SPINNER" });

        KpiService.snccsFeedbackFormDashboard(params)
            .then((res) => {
                const records = res?.data?.SCODE === "01" && Array.isArray(res.data.SDESC) ? [...res.data.SDESC].sort((a, b) => String(a.regular_session ?? "").localeCompare(String(b.regular_session ?? ""), undefined, { numeric: true, sensitivity: "base" }) || String(a.district_id ?? "").localeCompare(String(b.district_id ?? ""), undefined, { numeric: true, sensitivity: "base" }) || String(a.level_of_entry ?? "").localeCompare(String(b.level_of_entry ?? ""), undefined, { numeric: true, sensitivity: "base" })) : [];

                setAllRecords(records);
                setDetails((prev) => {
                    if (!prev?.title) return prev;

                    return {
                        ...prev,
                        records: (prev.records || []).map((old) => {
                            const updated = records.find(
                                (r) =>
                                    String(r.cfms_id) === String(old.cfms_id) &&
                                    String(r.name_of_topic) === String(old.name_of_topic)
                            );

                            return updated ? { ...old, ...updated } : old;
                        }),
                    };
                });

                setApiMessage(records.length ? "" : "No records found.");


                const submittedRecords = records.filter(isSubmitted);
                const evaluatedRecords = submittedRecords.filter(isEvaluated);

                // Evaluation-wise summary
                const evaluationMap = {};
                submittedRecords.forEach((record) => {
                    const id = levelId(record?.level_of_entry);
                    const level = levels.find((item) => item.id === id);

                    if (!level) return;

                    if (!evaluationMap[id]) {
                        evaluationMap[id] = {
                            id: level.id,
                            category: level.name,
                            submitted: 0,
                            evaluated: 0,
                            pendingEvaluation: 0,
                            percentage: "0.0",
                            records: [],
                        };
                    }

                    const row = evaluationMap[id];

                    row.submitted++;
                    row.records.push(record);

                    if (isEvaluated(record)) {
                        row.evaluated++;
                    } else {
                        row.pendingEvaluation++;
                    }
                });
                const evaluationRows = Object.values(evaluationMap).map((row) => ({ ...row, percentage: row.submitted ? ((row.evaluated / row.submitted) * 100).toFixed(1) : "0.0", })).sort((a, b) => Number(a.id) - Number(b.id));



                // Rating-wise summary
                const ratingMap = {};
                evaluatedRecords.forEach((record) => {
                    const rating = ratings.find((item) => gradeMatches(record, item));
                    if (!rating) return;
                    if (!ratingMap[rating]) {
                        ratingMap[rating] = { rating, secretariat: 0, hods: 0, districts: 0, total: 0, records: [], };
                    }
                    const row = ratingMap[rating];
                    const level = levelId(record?.level_of_entry);
                    if (level === "1") row.secretariat++;
                    else if (level === "2") row.hods++;
                    else if (level === "3") row.districts++;
                    row.total++;
                    row.records.push(record);
                });

                const ratingRows = ratings.filter((rating) => ratingMap[rating]).map((rating) => ratingMap[rating]);

                // Department-wise evaluation
                const departmentMap = {};

                submittedRecords.forEach((record) => {
                    const departmentId = String(
                        record?.dept_id ?? record?.dept_name ?? "Unknown"
                    );

                    if (!departmentMap[departmentId]) {
                        departmentMap[departmentId] = {
                            departmentId,
                            department: record?.dept_name || "Unknown Department",
                            deptcode: record?.deptcode ?? "",
                            submitted: 0,
                            evaluated: 0,
                            pendingEvaluation: 0,
                            Excellent: 0,
                            Good: 0,
                            Average: 0,
                            Poor: 0,
                            records: [],
                        };
                    }

                    const row = departmentMap[departmentId];
                    row.submitted++;
                    row.records.push(record);

                    if (isEvaluated(record)) {
                        row.evaluated++;
                        const rating = ratings.find((item) => gradeMatches(record, item));
                        if (rating) row[rating]++;
                    } else {
                        row.pendingEvaluation++;
                    }
                });

                const departmentRows = Object.values(departmentMap).sort(
                    (a, b) =>
                        String(a.deptcode).localeCompare(String(b.deptcode), undefined, {
                            numeric: true,
                            sensitivity: "base",
                        })
                );

                // District-wise evaluation
                const districtMap = {};

                submittedRecords.forEach((record) => {
                    const districtId = String(record?.district_id ?? "").trim();
                    const districtShortName = String(record?.district_short_name ?? "").trim();
                    const districtName = String(record?.district_name ?? "").trim();

                    if (!districtId || !districtName) return;

                    if (!districtMap[districtId]) {
                        districtMap[districtId] = {
                            districtId,
                            districtShort: districtShortName || districtName,
                            district: districtName,
                            submitted: 0,
                            evaluated: 0,
                            pendingEvaluation: 0,
                            Excellent: 0,
                            Good: 0,
                            Average: 0,
                            Poor: 0,
                            records: [],
                        };
                    }
                    const row = districtMap[districtId];

                    row.submitted += 1;
                    row.records.push(record);

                    if (isEvaluated(record)) {
                        row.evaluated += 1;

                        const rating = ratings.find((item) =>
                            gradeMatches(record, item)
                        );

                        if (rating) row[rating] += 1;
                    } else {
                        row.pendingEvaluation += 1;
                    }
                });

                const districtRows = Object.values(districtMap).sort(
                    (a, b) => Number(a.districtId) - Number(b.districtId)
                );

                // Theme-wise evaluation
                const themeMap = {};

                submittedRecords.forEach((record) => {
                    const topicId = String(record?.name_of_topic ?? "").trim();

                    if (!["1", "2", "3", "4"].includes(topicId)) return;

                    if (!themeMap[topicId]) {
                        themeMap[topicId] = {
                            topicId: Number(topicId),
                            theme: `Regular Session ${topicId}`,
                            submitted: 0,
                            evaluated: 0,
                            pendingEvaluation: 0,
                            Excellent: 0,
                            Good: 0,
                            Average: 0,
                            Poor: 0,
                            records: [],
                            uniqueOfficers: new Map(),
                        };
                    }

                    const row = themeMap[topicId];
                    const cfmsId = String(record?.cfms_id ?? "").trim();

                    if (cfmsId && !row.uniqueOfficers.has(cfmsId)) {
                        row.uniqueOfficers.set(cfmsId, record);
                    }
                });

                const themeRows = Object.values(themeMap).map((row) => {
                    const uniqueRecords = [...row.uniqueOfficers.values()];
                    const evaluatedRecords = uniqueRecords.filter(isEvaluated);

                    return {
                        ...row,
                        submitted: uniqueRecords.length,
                        evaluated: evaluatedRecords.length,
                        pendingEvaluation: uniqueRecords.length - evaluatedRecords.length,
                        Excellent: uniqueRecords.filter((r) => gradeMatches(r, "Excellent")).length,
                        Good: uniqueRecords.filter((r) => gradeMatches(r, "Good")).length,
                        Average: uniqueRecords.filter((r) => gradeMatches(r, "Average")).length,
                        Poor: uniqueRecords.filter((r) => gradeMatches(r, "Poor")).length,
                        records: uniqueRecords,
                    };
                }).map(({ uniqueOfficers, ...row }) => row)
                    .sort((a, b) => a.topicId - b.topicId);

                setData({ evaluationRows, ratingRows, departmentRows, districtRows, themeRows, });

            })
            .catch(() => {
                setAllRecords([]);

                setApiMessage("Unable to load dashboard data.");
                setData({
                    evaluationRows: levels.map((level) => ({ id: level.id, category: level.name, submitted: 0, evaluated: 0, pendingEvaluation: 0, percentage: "0.0", records: [], })),
                    ratingRows: ratings.map((rating) => ({ rating, secretariat: 0, hods: 0, districts: 0, total: 0, records: [], })),
                    departmentRows: [],
                    districtRows: [],
                    themeRows: [],
                });
            })
            .finally(() => {
                store.dispatch({ type: "HIDE_SPINNER" });
            });

        const handleDownload = (e) => {
            if (e.detail) downloadpdf(e.detail);
        };

        window.addEventListener("feedback-book-download", handleDownload);

        return () => {
            window.removeEventListener("feedback-book-download", handleDownload);
        };

    }, [refreshDashboard]);





    const toggleSort = (tableId, key) => {
        setTableSort((previous) => ({
            ...previous,
            [tableId]: {
                key,
                direction:
                    previous[tableId]?.key === key &&
                        previous[tableId]?.direction === "asc"
                        ? "desc"
                        : "asc",
            },
        }));
    };

    const sortRows = (rows, tableId) => {
        const search = tableSearch[tableId]?.trim().toLowerCase() || "";

        const filteredRows = rows.filter((row) =>
            Object.entries(row).some(
                ([key, value]) =>
                    key !== "records" &&
                    String(value ?? "").toLowerCase().includes(search)
            )
        );

        const config = tableSort[tableId];

        if (!config?.key) return filteredRows;

        return [...filteredRows].sort((a, b) => {
            const x = a[config.key] ?? "";
            const y = b[config.key] ?? "";
            const nx = Number(x);
            const ny = Number(y);

            const result =
                String(x).trim() !== "" &&
                    String(y).trim() !== "" &&
                    Number.isFinite(nx) &&
                    Number.isFinite(ny)
                    ? nx - ny
                    : String(x).localeCompare(String(y), undefined, {
                        numeric: true,
                        sensitivity: "base",
                    });

            return config.direction === "asc" ? result : -result;
        });
    };



    const cellStyle = {
        padding: "9px 10px",
        border: "1px solid #e1e7ef",
        fontSize: 12,
        color: "#34445a",
        verticalAlign: "middle",
    };

    const headerStyle = {
        ...cellStyle,
        background: "#edf2f8",
        color: "#334760",
        textAlign: "left",
        whiteSpace: "nowrap",
        cursor: "pointer",
        userSelect: "none",
    };

    const cardStyle = {
        minWidth: 0,
        background: "#fff",
        border: "1px solid #dfe6ef",
        borderRadius: 10,
        padding: 12,
        boxShadow: "0 2px 8px rgba(28,48,75,0.06)",
    };

    const searchStyle = {
        width: 175,
        maxWidth: "100%",
        padding: "7px 9px",
        border: "1px solid #cbd5e1",
        borderRadius: 6,
        outline: "none",
        fontSize: 12,
    };



    const openDetails = (title, records) => {
        setDetails({
            title,
            records: Array.isArray(records) ? records : [],
        });
        setTableSearch((prev) => ({ ...prev, details: "" }));

    };

    const closeDetails = () => {
        setDetails({ title: "", records: [] });
        setTableSearch((prev) => ({ ...prev, details: "" }));

    };

    const filteredDetails = details.records.filter((record) => {
        const search = detailSearch.trim().replace(/\s+/g, " ").toLowerCase();

        if (!search) return true;

        const values = [
            record.cfms_id,
            record.f_name,
            record.designation,
            record.level_of_entry,
            record.deptcode,
            record.dept_name,
            record.district_name,
            record.mobile,
            record.current_posting,
            record.regular_session,
            isSubmitted(record) ? "Submitted" : "Draft",
            record.updated_time,
            record.is_eval_required === true ? "Yes" : "No",
            record.eval_grade,
        ];

        return values.some((value) =>
            String(value ?? "")
                .trim()
                .replace(/\s+/g, " ")
                .toLowerCase()
                .includes(search)
        );
    });



    const renderRatingCard = (name, icon, bg, border, accent, stars) => {
        const row = data.ratingRows.find((r) => r.rating === name);
        const count = row?.total ?? 0;

        return (
            <div
                key={name}
                style={{
                    minWidth: 0,
                    padding: "9px 10px",
                    borderRadius: 10,
                    background: bg,
                    border: `1px solid ${border}`,
                    boxShadow: "0 3px 9px rgba(30,45,65,0.08)",
                }}
            >
                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 3,
                    }}
                >
                    <div
                        style={{
                            width: 34,
                            height: 34,
                            minWidth: 34,
                            borderRadius: "50%",
                            background: "#FFFFFF",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: 17,
                            color: accent,
                            boxShadow: "0 2px 5px rgba(0,0,0,0.08)",
                        }}
                    >
                        {icon}
                    </div>

                    <div style={{ display: "flex", gap: 2, alignItems: "center" }}>
                        {[1, 2, 3, 4, 5].map((star) => (
                            <span
                                key={star}
                                style={{
                                    color: star <= stars ? accent : `${accent}45`,
                                    fontSize: 11,
                                    lineHeight: 1,
                                }}
                            >
                                ★
                            </span>
                        ))}
                    </div>
                </div>

                <div
                    style={{
                        color: "#26364B",
                        fontSize: 13,
                        fontWeight: 800,
                        marginTop: 6,
                    }}
                >
                    {name}
                </div>

                <div
                    style={{
                        color: accent,
                        fontSize: 25,
                        fontWeight: 900,
                        lineHeight: 1.15,
                        marginTop: 2,
                    }}
                >
                    {count.toLocaleString("en-IN")}
                </div>


            </div>
        );
    };

    const exportToExcel = (tableId, fileName) => {
        const table = document.getElementById(tableId);
        if (!table) return;

        const workbook = XLSX.utils.table_to_book(table, { sheet: "Report" });
        XLSX.writeFile(workbook, `${fileName}.xlsx`);
    };


    const getEntryLevel = (value) => ["Secretariat", "State HOD", "District", "Constituency", "Division", "Mandal", "Village",][Number(value) - 1] || "-";

    const RATING_COLORS = [
        {
            name: "Excellent",
            color: "#16804a",
            dark: "#0f6037",
            top: "#43a875",
        },
        {
            name: "Good",
            color: "#245db0",
            dark: "#194582",
            top: "#6390d0",
        },
        {
            name: "Average",
            color: "#9a6700",
            dark: "#754f00",
            top: "#c99a35",
        },
        {
            name: "Poor",
            color: "#c03939",
            dark: "#922b2b",
            top: "#df7777",
        },
    ];

    const RatingEvaluationChart = ({ rows = [], onRatingClick }) => {
        const allRecords = rows.flatMap((row) => row.records || []);

        const slices = RATING_COLORS.map((rating) => ({
            ...rating,
            name: rating.name,
            theme: "All Sessions",
            value: rows.reduce(
                (sum, row) => sum + Number(row[rating.name] || 0),
                0
            ),
            records: allRecords,
        }));

        const total = slices.reduce((sum, slice) => sum + slice.value, 0);

        let angle = -90;

        const chartSlices = slices.map((slice) => {
            const start = angle;
            angle += total ? (slice.value / total) * 360 : 0;

            return {
                ...slice,
                start,
                end: angle,
            };
        });

        const point = (radius, degrees, depth = 0) => {
            const rad = (degrees * Math.PI) / 180;
            return [
                120 + radius * Math.cos(rad),
                100 + radius * Math.sin(rad) + depth,
            ];
        };

        const getPath = (start, end, depth = 0) => {
            const sweep = end - start;
            if (sweep >= 359.99) return "";

            const [x1, y1] = point(75, start, depth);
            const [x2, y2] = point(75, end, depth);
            const [x3, y3] = point(42, end, depth);
            const [x4, y4] = point(42, start, depth);
            const large = sweep > 180 ? 1 : 0;

            return `M ${x1} ${y1} A 75 75 0 ${large} 1 ${x2} ${y2}
            L ${x3} ${y3} A 42 42 0 ${large} 0 ${x4} ${y4} Z`;
        };

        return (
            <div
                style={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "flex-start",
                    padding: "4px 2px 8px",
                    minWidth: 0,
                }}
            >
                <svg
                    width="180"
                    height="180"
                    viewBox="0 0 240 220"
                    style={{
                        display: "block",
                        margin: "0 auto",
                        flexShrink: 0,
                        maxWidth: "100%",
                    }}
                >
                    {/* 3D depth */}
                    {chartSlices.map(
                        (slice) =>
                            slice.value > 0 && (
                                <path
                                    key={`${slice.name}-depth`}
                                    d={getPath(slice.start, slice.end, 9)}
                                    fill={slice.dark}
                                    onClick={() => onRatingClick?.(slice)}
                                    style={{ cursor: "pointer" }}
                                />
                            )
                    )}

                    {/* Exactly four rating partitions */}
                    {chartSlices.map((slice) => {
                        if (!slice.value || !total) return null;

                        const percentage = (slice.value / total) * 100;
                        const fullCircle = slice.end - slice.start >= 359.99;
                        const middle = (slice.start + slice.end) / 2;

                        return (
                            <g
                                key={slice.name}
                                onClick={() => onRatingClick?.(slice)}
                                style={{ cursor: "pointer" }}
                            >
                                {fullCircle ? (
                                    <circle
                                        cx="120"
                                        cy="100"
                                        r="58"
                                        fill="none"
                                        stroke={slice.color}
                                        strokeWidth="33"
                                    />
                                ) : (
                                    <path
                                        d={getPath(slice.start, slice.end)}
                                        fill={slice.color}
                                        stroke="#fff"
                                        strokeWidth="1.5"
                                    />
                                )}

                                {percentage >= 5 && (
                                    <text
                                        x={
                                            120 +
                                            59 * Math.cos((middle * Math.PI) / 180)
                                        }
                                        y={
                                            100 +
                                            59 * Math.sin((middle * Math.PI) / 180)
                                        }
                                        textAnchor="middle"
                                        dominantBaseline="middle"
                                        fill={
                                            slice.name === "Poor"
                                                ? "#263238"
                                                : "#fff"
                                        }
                                        fontSize="10"
                                        fontWeight="bold"
                                        pointerEvents="none"
                                    >
                                        {percentage.toFixed(1)}%
                                    </text>
                                )}
                            </g>
                        );
                    })}

                    <circle cx="120" cy="100" r="39" fill="#fff" />

                    <text
                        x="120"
                        y="96"
                        textAnchor="middle"
                        fontSize="11"
                        fill="#64748b"
                    >
                        Evaluated
                    </text>

                    <text
                        x="120"
                        y="117"
                        textAnchor="middle"
                        fontSize="22"
                        fontWeight="bold"
                        fill="#263d5b"
                    >
                        {total}
                    </text>
                </svg>

                {/* Four fixed rating legend items */}
                <div
                    style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        flexWrap: "wrap",
                        gap: "8px 10px",
                        marginTop: 8,
                    }}
                >
                    {chartSlices.map((slice) => {
                        const percentage = total
                            ? (slice.value / total) * 100
                            : 0;

                        return (
                            <div
                                key={slice.name}
                                role="button"
                                tabIndex={0}
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 4,
                                    fontSize: 10,
                                    cursor: "pointer",
                                }}
                                onClick={() => onRatingClick?.(slice)}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter" || e.key === " ") {
                                        e.preventDefault();
                                        onRatingClick?.(slice);
                                    }
                                }}
                            >
                                <span
                                    style={{
                                        width: 9,
                                        height: 9,
                                        borderRadius: 2,
                                        background: slice.color,
                                        flexShrink: 0,
                                    }}
                                />
                                <span>{slice.name}</span>
                                <strong>{slice.value}</strong>
                                <span style={{ color: "#64748b" }}>
                                    ({percentage.toFixed(1)}%)
                                </span>
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    };


    const ThemeEvaluationDonut = ({ rows = [], onThemeClick }) => {
        const colors = [
            "#FF5C5C",
            "#38BDF8",
            "#7C3AED",
            "#D4E157",
        ];
        const total = rows.reduce(
            (sum, row) => sum + Number(row.evaluated || 0),
            0
        );

        let angle = -90;

        const slices = rows.map((row, index) => {
            const value = Number(row.evaluated || 0);
            const start = angle;
            angle += total ? (value / total) * 360 : 0;

            return {
                ...row,
                value,
                start,
                end: angle,
                color: colors[index % colors.length],
            };
        });

        const point = (radius, degrees) => {
            const rad = (degrees * Math.PI) / 180;
            return [
                120 + radius * Math.cos(rad),
                100 + radius * Math.sin(rad),
            ];
        };

        const getPath = (start, end) => {
            const sweep = end - start;
            if (sweep >= 359.99) return "";

            const [x1, y1] = point(75, start);
            const [x2, y2] = point(75, end);
            const [x3, y3] = point(43, end);
            const [x4, y4] = point(43, start);
            const large = sweep > 180 ? 1 : 0;

            return `M ${x1} ${y1} A 75 75 0 ${large} 1 ${x2} ${y2}
            L ${x3} ${y3} A 43 43 0 ${large} 0 ${x4} ${y4} Z`;
        };

        return (
            <div
                style={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "flex-start",
                    padding: "4px 2px 8px",
                    minWidth: 0,
                }}
            >
                <svg
                    width="180"
                    height="180"
                    viewBox="0 0 220 220"
                    style={{
                        display: "block",
                        margin: "0 auto",
                        flexShrink: 0,
                        maxWidth: "100%",
                    }}
                >
                    {slices.map((slice) => {
                        if (!slice.value || !total) return null;

                        const percentage = (slice.value / total) * 100;
                        const fullCircle = slice.end - slice.start >= 359.99;
                        const middle = (slice.start + slice.end) / 2;

                        return (
                            <g
                                key={slice.topicId}
                                onClick={() => onThemeClick?.(slice)}
                                style={{ cursor: "pointer" }}
                            >
                                {fullCircle ? (
                                    <circle
                                        cx="120"
                                        cy="100"
                                        r="59"
                                        fill="none"
                                        stroke={slice.color}
                                        strokeWidth="32"
                                    />
                                ) : (
                                    <path
                                        d={getPath(slice.start, slice.end)}
                                        fill={slice.color}
                                        stroke="#fff"
                                        strokeWidth="1.5"
                                    />
                                )}

                                {percentage >= 5 && (
                                    <text
                                        x={
                                            120 +
                                            59 *
                                            Math.cos(
                                                (middle * Math.PI) / 180
                                            )
                                        }
                                        y={
                                            100 +
                                            59 *
                                            Math.sin(
                                                (middle * Math.PI) / 180
                                            )
                                        }
                                        textAnchor="middle"
                                        dominantBaseline="middle"
                                        fill="#fff"
                                        fontSize="10"
                                        fontWeight="bold"
                                        pointerEvents="none"
                                    >
                                        {percentage.toFixed(1)}%
                                    </text>
                                )}
                            </g>
                        );
                    })}

                    <circle cx="120" cy="100" r="41" fill="#fff" />

                    <text
                        x="120"
                        y="94"
                        textAnchor="middle"
                        fontSize="10"
                        fill="#64748b"
                    >
                        Evaluated
                    </text>

                    <text
                        x="120"
                        y="116"
                        textAnchor="middle"
                        fontSize="22"
                        fontWeight="bold"
                        fill="#263d5b"
                    >
                        {total}
                    </text>
                </svg>

                <div
                    style={{
                        display: "flex",
                        flexWrap: "wrap",
                        justifyContent: "center",
                        alignItems: "center",
                        gap: "8px 10px",
                        marginTop: 8,
                    }}
                >
                    {slices.map((slice) => {
                        const percentage = total
                            ? (slice.value / total) * 100
                            : 0;

                        return (
                            <div
                                key={slice.topicId}
                                role="button"
                                tabIndex={0}
                                onClick={() => onThemeClick?.(slice)}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter" || e.key === " ") {
                                        e.preventDefault();
                                        onThemeClick?.(slice);
                                    }
                                }}
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 4,
                                    fontSize: 10,
                                    cursor: "pointer",
                                }}
                            >
                                <span
                                    style={{
                                        width: 9,
                                        height: 9,
                                        borderRadius: 2,
                                        background: slice.color,
                                        flexShrink: 0,
                                    }}
                                />
                                <span>{slice.theme}</span>
                                <strong>{slice.value}</strong>
                                <span style={{ color: "#64748b" }}>
                                    ({percentage.toFixed(1)}%)
                                </span>
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    };


    const DepartmentRating3DChart = ({ rows = [], onRatingClick }) => {
        const ratings = [
            {
                name: "Excellent",
                color: "#16804a",
                dark: "#0f6037",
                top: "#43a875",
            },
            {
                name: "Good",
                color: "#245db0",
                dark: "#194582",
                top: "#6390d0",
            },
            {
                name: "Average",
                color: "#9a6700",
                dark: "#754f00",
                top: "#c99a35",
            },
            {
                name: "Poor",
                color: "#c03939",
                dark: "#922b2b",
                top: "#df7777",
            },
        ];

        const max = Math.max(
            1,
            ...rows.map((row) =>
                ratings.reduce(
                    (sum, rating) => sum + Number(row[rating.name] || 0),
                    0
                )
            )
        );

        const baseY = 185;
        const chartHeight = 140;
        const scale = chartHeight / max;
        const barWidth = 22;
        const depth = 6;
        const gap = 1;
        const width = Math.max(500, rows.length * 65 + 50);

        return (
            <div
                style={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    minHeight: 0,
                }}
            >
                <div
                    style={{
                        display: "flex",
                        gap: 9,
                        flexWrap: "wrap",
                        marginBottom: 8,
                        flexShrink: 0,
                    }}
                >
                    {ratings.map((rating) => (
                        <span
                            key={rating.name}
                            style={{ fontSize: 10 }}
                        >
                            <span style={{ color: rating.color }}>■</span>{" "}
                            {rating.name}
                        </span>
                    ))}
                </div>

                <div
                    style={{
                        overflowX: "auto",
                        overflowY: "hidden",
                        flex: 1,
                        minHeight: 0,
                    }}
                >
                    <svg
                        width={width}
                        height="255"
                        viewBox={`0 0 ${width} 255`}
                        style={{ display: "block" }}
                    >
                        {/* X-axis baseline */}
                        <line
                            x1="20"
                            y1={baseY}
                            x2={width - 15}
                            y2={baseY}
                            stroke="#94a3b8"
                            strokeWidth="1.2"
                        />
                        {rows.map((row, index) => {
                            const x = 35 + index * 65;
                            let y = baseY;

                            const dept =
                                row.deptcode ??
                                row.dept_code ??
                                row.deptCode ??
                                "-";

                            return (
                                <g key={`${dept}-${index}`}>
                                    {ratings.map((rating) => {
                                        const value = Number(
                                            row[rating.name] || 0
                                        );

                                        if (!value) return null;

                                        const height = Math.max(12, value * scale);
                                        y -= height;

                                        const boxY = y + gap / 2;
                                        const boxH = Math.max(2, height - gap);

                                        return (
                                            <g
                                                key={rating.name}
                                                onClick={() =>
                                                    onRatingClick?.(
                                                        row,
                                                        rating.name
                                                    )
                                                }
                                                style={{ cursor: "pointer" }}
                                            >
                                                <title>
                                                    {`${dept} - ${rating.name}: ${value}`}
                                                </title>

                                                <rect
                                                    x={x}
                                                    y={boxY}
                                                    width={barWidth}
                                                    height={boxH}
                                                    fill={rating.color}
                                                />

                                                <polygon
                                                    points={`${x + barWidth},${boxY} ${x + barWidth + depth
                                                        },${boxY - depth} ${x + barWidth + depth
                                                        },${boxY + boxH - depth} ${x + barWidth
                                                        },${boxY + boxH}`}
                                                    fill={rating.dark}
                                                />

                                                <polygon
                                                    points={`${x},${boxY} ${x + depth
                                                        },${boxY - depth} ${x + barWidth + depth
                                                        },${boxY - depth} ${x + barWidth
                                                        },${boxY}`}
                                                    fill={rating.top}
                                                />

                                                <text
                                                    x={x + barWidth / 2}
                                                    y={boxY + boxH / 2}
                                                    textAnchor="middle"
                                                    dominantBaseline="middle"
                                                    fontSize={boxH < 14 ? "8" : "9"}
                                                    fontWeight="bold"
                                                    fill="#fff"
                                                    stroke={boxH < 14 ? rating.dark : "none"}
                                                    strokeWidth={boxH < 14 ? "0.5" : "0"}
                                                    paintOrder="stroke"
                                                    pointerEvents="none"
                                                >
                                                    {value}
                                                </text>
                                            </g>
                                        );
                                    })}

                                    <text
                                        x={x + barWidth / 2 + depth / 2}
                                        y="215"
                                        textAnchor="middle"
                                        fontSize="9"
                                        fill="#475569"
                                    >
                                        {dept}
                                    </text>
                                </g>
                            );
                        })}

                        <text
                            x={width / 2}
                            y="240"
                            textAnchor="middle"
                            fontSize="9"
                            fill="#475569"
                        >
                            Department Code
                        </text>
                    </svg>
                </div>
            </div>
        );
    };

    return (
        <>
            <Menubar />


            <Container fluid style={{ width: "97%" }}>


                <div className="sa-title">
                    <h2>
                        SNCCS Feedback Evaluation<span> Dashboard</span>
                    </h2>
                </div>
                {apiMessage && (
                    <div style={{ padding: 10, marginBottom: 12, borderRadius: 6, background: "#fff", border: "1px solid #dfe6ef", color: "#64748b", fontSize: 13 }}>
                        {apiMessage}
                    </div>
                )}
            </Container>

            {details.title ? (
                <Container fluid style={{ width: "95%" }} className="outer-page-content-container" >
                    <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center" }}>
                        <button
                            type="button"
                            className="btn btn-sm btn-secondary"
                            onClick={closeDetails}
                        >
                            ← Back
                        </button>
                    </div>

                    <div className="tpd-panel">
                        <div className="tpd-panel-title tpd-title-flex">
                            <span> {details.title}</span>
                            <div style={{ display: "flex", alignItems: "center", gap: "6px", marginLeft: "auto" }}>
                                <div style={{ display: "flex", alignItems: "center", gap: "5px", width: "110px", padding: "3px 8px", border: "1px solid #ddd", borderRadius: "15px", background: "#fff" }}>
                                    <AiOutlineSearch style={{ fontSize: "13px", color: "#888", flexShrink: 0 }} />
                                    <input type="search" value={tableSearch.details} onChange={(e) => setTableSearch((p) => ({ ...p, details: e.target.value }))} style={{ width: "100%", minWidth: 0, border: "none", outline: "none", fontSize: "10px", background: "transparent" }} />
                                </div>

                                <button type="button" className="btn btn-sm btn-light border" style={{ padding: "1px 2px" }} title="Export to Excel" onClick={() => exportToExcel("detailsTable", "Details")} > <FaFileExcel style={{ color: "#198754" }} /> </button>
                            </div>
                        </div>


                        <div className="tpd-table-wrapper">
                            <table className="tpd-table tpd-officers" id="detailsTable">
                                <thead>
                                    <tr>
                                        <th>S.No</th>

                                        {[

                                            ["regular_session", "Session"],
                                            ["level_of_entry", "Entry Level"],
                                            ["department", "Department"],
                                            ["district_name", "District"],

                                            ["cfms_id", "CFMS ID"],
                                            ["f_name", "Officer Name"],
                                            ["designation", "Designation"],

                                            ["mobile", "Mobile"],
                                            ["current_posting", "Current Posting"],
                                            ["status", "Status"],
                                            ["updated_time", "Submitted Time"],
                                            ["rating", "Rating"],
                                        ].map(([key, label]) => (
                                            <th
                                                key={key}
                                                onClick={() => toggleSort("details", key)}
                                                className="tpd-sortable"
                                                style={{ cursor: "pointer" }}
                                            >
                                                <span className="tpd-th-inner">
                                                    {label}
                                                    <span className="tpd-sort-icon">
                                                        <i
                                                            className={
                                                                tableSort.details?.key === key &&
                                                                    tableSort.details?.direction === "asc"
                                                                    ? "up active"
                                                                    : "up"
                                                            }
                                                        />
                                                        <i
                                                            className={
                                                                tableSort.details?.key === key &&
                                                                    tableSort.details?.direction === "desc"
                                                                    ? "down active"
                                                                    : "down"
                                                            }
                                                        />
                                                    </span>
                                                </span>
                                            </th>
                                        ))}

                                        <th>Evaluation</th>
                                    </tr>
                                </thead>

                                <tbody>
                                    {sortRows(details.records, "details").length > 0 ? (
                                        sortRows(details.records, "details").map((record, index) => (
                                            <tr key={record.cfms_id || index}>
                                                <td>{index + 1}</td>
                                                <td>{record.regular_session ?? "-"}</td>
                                                <td>{getEntryLevel(record.level_of_entry)}</td>
                                                <td>
                                                    {record.deptcode}
                                                    <br />
                                                    {record.dept_name || "-"}
                                                </td>
                                                <td>{record.district_name ?? "-"}</td>
                                                <td>{record.cfms_id ?? "-"}</td>
                                                <td>{record.f_name ?? "-"}</td>
                                                <td>{record.designation ?? "-"}</td>

                                                <td>{record.mobile ?? "-"}</td>
                                                <td>{record.current_posting ?? "-"}</td>
                                                <td>
                                                    <span
                                                        className={`tpd-badge ${isSubmitted(record)
                                                            ? "tpd-badge-final"
                                                            : "tpd-badge-draft"
                                                            }`}
                                                    >
                                                        {isSubmitted(record) ? "Submitted" : "Draft"}
                                                    </span>
                                                </td>
                                                <td>
                                                    {record.updated_time
                                                        ? new Date(record.updated_time).toLocaleString("en-GB", {
                                                            day: "2-digit",
                                                            month: "short",
                                                            year: "numeric",
                                                            hour: "2-digit",
                                                            minute: "2-digit",
                                                            hour12: true,
                                                            timeZone: "Asia/Kolkata",
                                                        })
                                                        : "-"}
                                                </td>
                                                <td>
                                                    {(() => {
                                                        const grade = String(record.eval_grade ?? "").trim();
                                                        const rating = RATING_COLORS.find(
                                                            (item) => item.name === grade
                                                        );

                                                        return (
                                                            <span
                                                                style={{
                                                                    display: "inline-block",
                                                                    padding: "4px 12px",
                                                                    borderRadius: "20px",
                                                                    fontSize: "11px",
                                                                    fontWeight: 600,
                                                                    color: rating?.color || "#64748b",
                                                                    background: rating
                                                                        ? `${rating.color}18`
                                                                        : "#64748b18",
                                                                    border: `1px solid ${rating?.color || "#64748b"}50`,
                                                                    whiteSpace: "nowrap",
                                                                }}
                                                            >
                                                                {grade || "Not Evaluated"}
                                                            </span>
                                                        );
                                                    })()}
                                                </td>
                                                <td style={{ textAlign: "center", whiteSpace: "nowrap" }}>
                                                    <button
                                                        type="button"
                                                        onClick={() => downloadpdf(record, true)}
                                                        style={{
                                                            border: "none",
                                                            background: "transparent",
                                                            color: "#176B5B",
                                                            cursor: "pointer",
                                                        }}
                                                    >
                                                        {record.is_eval_required === true &&
                                                            record.eval_grade == null ? (
                                                            <TbPencilStar size={20} style={{ color: "blue" }} title="Evaluation" />
                                                        ) : (
                                                            <FaEye title="View" size={20} />
                                                        )}
                                                    </button>
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan={14} style={{ ...cellStyle, textAlign: "center" }}>
                                                No records found
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>



                    <FeedbackBookViewer showRating={true} onClose={() => { setRefreshDashboard((prev) => prev + 1); }} />

                </Container>
            ) : (


                <Container fluid style={{ width: "97%" }} className="outer-page-content-container">
                    <Row className="g-2 align-items-center">
                        <Col lg={3} md={3} className="d-flex justify-content-center">
                            <Lottie src={ratingAnimation} autoplay loop style={{ width: 170, height: 170 }} />
                        </Col>

                        <Col lg={9} md={9}>
                            <div
                                style={{
                                    display: "grid",
                                    gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
                                    gap: 8,
                                    marginBottom: 18,
                                }}
                            >
                                {renderRatingCard("Excellent", "🏆", "#D9F0E5", "#8ACBAA", "#087A50", 5,)}
                                {renderRatingCard("Good", "↗", "#DDE8FC", "#9AB8F2", "#2454B5", 4,)}
                                {renderRatingCard("Average", "◒", "#EAE0FA", "#BCA2E8", "#693BAA", 3,)}
                                {renderRatingCard("Poor", "!", "#FBE2DC", "#ECA99B", "#B9402B", 1,)}
                            </div>
                        </Col>
                    </Row>

                    {/* 1. REGISTRATION SUMMARY */}
                    <Row className="g-1 mb-3">
                        {/* THEME-WISE EVALUATION REPORT */}

                        <Col xs={12} lg={6}>
                            <div className="tpd-panel">
                                <div className="tpd-panel-title tpd-title-flex">
                                    <span>Theme-wise Evaluation</span>
                                    <div style={{ display: "flex", alignItems: "center", gap: "6px", marginLeft: "auto" }}>
                                        <div style={{ display: "flex", alignItems: "center", gap: "5px", width: "110px", padding: "3px 8px", border: "1px solid #ddd", borderRadius: "15px", background: "#fff" }}>
                                            <AiOutlineSearch style={{ fontSize: "13px", color: "#888", flexShrink: 0 }} />
                                            <input type="search" value={tableSearch.theme} onChange={(e) => setTableSearch((p) => ({ ...p, theme: e.target.value }))} style={{ width: "100%", minWidth: 0, border: "none", outline: "none", fontSize: "10px", background: "transparent" }} />
                                        </div>

                                        <button type="button" className="btn btn-sm btn-light border" style={{ padding: "1px 2px" }} title="Export to Excel" onClick={() => exportToExcel("themeEvaluationTable", "Theme-wise-Evaluation")} > <FaFileExcel style={{ color: "#198754" }} /> </button>
                                    </div>
                                </div>


                                <div className="tpd-panel-body tpd-scroll" style={{ overflow: "auto", maxHeight: 340, padding: 0 }} >
                                    <table id="themeEvaluationTable" className="tpd-table" style={{ width: "100%", minWidth: "max-content" }} >
                                        <thead>
                                            <tr>
                                                {[
                                                    ["theme", "Regular Session"],
                                                    ["submitted", "Submitted"],
                                                    ["evaluated", "Evaluated"],
                                                    ["pendingEvaluation", "Pending Evaluation"],
                                                    ...ratings.map((rating) => [rating, rating]),
                                                ].map(([key, label]) => (
                                                    <th
                                                        key={key}
                                                        className="tpd-sortable"
                                                        onClick={() => toggleSort("theme", key)}
                                                    >
                                                        <span className="tpd-th-inner">
                                                            {label}
                                                            <span className={`tpd-sort-icon ${tableSort.theme?.key === key
                                                                ? tableSort.theme.direction
                                                                : ""
                                                                }`}>
                                                                <i className="up" />
                                                                <i className="down" />
                                                            </span>
                                                        </span>
                                                    </th>
                                                ))}
                                            </tr>
                                        </thead>

                                        <tbody>
                                            {sortRows(data.themeRows, "theme").length ? (
                                                sortRows(data.themeRows, "theme").map((row) => (
                                                    <tr key={row.topicId}>
                                                        <td style={{ textAlign: "left" }}>{row.theme}</td>
                                                        <td style={{ textAlign: "right" }}>
                                                            <button
                                                                type="button"
                                                                disabled={!row.submitted}
                                                                onClick={() =>
                                                                    openDetails(
                                                                        `${row.theme} - Submitted`,
                                                                        row.records
                                                                    )
                                                                }
                                                                style={{ color: row.submitted ? "#2463b5" : "inherit", fontWeight: 600, cursor: row.submitted ? "pointer" : "default" }}
                                                            >
                                                                {row.submitted}
                                                            </button>
                                                        </td>

                                                        <td style={{ textAlign: "right" }}>
                                                            <button
                                                                type="button"
                                                                disabled={!row.evaluated}
                                                                onClick={() =>
                                                                    openDetails(
                                                                        `${row.theme} - Evaluated`,
                                                                        row.records.filter(isEvaluated)
                                                                    )
                                                                }
                                                                style={{ color: row.evaluated ? "#2463b5" : "inherit", fontWeight: 600, cursor: row.evaluated ? "pointer" : "default" }}

                                                            >
                                                                {row.evaluated}
                                                            </button>
                                                        </td>
                                                        <td style={{ textAlign: "right" }}>
                                                            <button type="button" disabled={!row.pendingEvaluation} onClick={() => openDetails(`${row.theme} - Pending Evaluation`, row.records.filter((record) => !isEvaluated(record)))} style={{ color: row.pendingEvaluation ? "#2463b5" : "inherit", fontWeight: 600, cursor: row.pendingEvaluation ? "pointer" : "default" }} >
                                                                {row.pendingEvaluation}
                                                            </button>
                                                        </td>

                                                        {ratings.map((rating) => (
                                                            <td key={rating} style={{ textAlign: "right" }}>
                                                                <button
                                                                    type="button"
                                                                    onClick={() =>
                                                                        openDetails(
                                                                            `${row.theme} — ${rating}`,
                                                                            row.records.filter(
                                                                                (r) =>
                                                                                    isEvaluated(r) &&
                                                                                    gradeMatches(r, rating)
                                                                            )
                                                                        )
                                                                    }
                                                                    disabled={!row[rating]}
                                                                    style={{
                                                                        padding: 0, border: 0, background: "transparent",
                                                                        color: row[rating] ? "#2463b5" : "inherit",
                                                                        fontWeight: 600,
                                                                        cursor: row[rating] ? "pointer" : "default",
                                                                    }}
                                                                >
                                                                    {row[rating]}
                                                                </button>
                                                            </td>
                                                        ))}
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan={4 + ratings.length} style={{ textAlign: "center" }}>
                                                        No matching records
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                        <tfoot>
                                            <tr style={{ position: "sticky", bottom: 0, background: "#eaf1fb", fontWeight: 800 }}>
                                                <td style={{ textAlign: "left" }}>Total</td>
                                                {["submitted", "evaluated", "pendingEvaluation", ...ratings].map((key) => (
                                                    <td key={key} style={{ textAlign: "right" }}>
                                                        {sortRows(data.themeRows, "theme").reduce(
                                                            (sum, row) => sum + Number(row[key] || 0), 0
                                                        )}
                                                    </td>
                                                ))}
                                            </tr>
                                        </tfoot>
                                    </table>
                                </div>
                            </div>
                        </Col>

                        <Col xs={12} lg={6} >
                            <div className="tpd-panel">
                                <div className="tpd-panel-title tpd-title-flex">
                                    <span>Rating Summary</span>
                                    <div style={{ display: "flex", alignItems: "center", gap: "6px", marginLeft: "auto" }}>
                                        <div style={{ display: "flex", alignItems: "center", gap: "5px", width: "110px", padding: "3px 8px", border: "1px solid #ddd", borderRadius: "15px", background: "#fff" }}>
                                            <AiOutlineSearch style={{ fontSize: "13px", color: "#888", flexShrink: 0 }} />
                                            <input type="search" value={tableSearch.rating} onChange={(e) => setTableSearch((p) => ({ ...p, rating: e.target.value }))} style={{ width: "100%", minWidth: 0, border: "none", outline: "none", fontSize: "10px", background: "transparent" }} />
                                        </div>

                                        <button type="button" className="btn btn-sm btn-light border" style={{ padding: "1px 2px" }} title="Export to Excel" onClick={() => exportToExcel("ratingTable", "Rating-Summary")} > <FaFileExcel style={{ color: "#198754" }} /> </button>
                                    </div>
                                </div>



                                <div className="tpd-panel-body tpd-scroll" style={{ overflow: "auto", maxHeight: 340, padding: 0 }}>
                                    <table className="tpd-table" id="ratingTable" style={{ width: "100%", minWidth: "max-content" }} >
                                        <thead>
                                            <tr>
                                                {[
                                                    ["rating", "Rating"],
                                                    ["secretariat", "Secretariat"],
                                                    ["hods", "HOD"],
                                                    ["districts", "District"],
                                                    ["total", "Total"],
                                                ].map(([key, label]) => (
                                                    <th key={key} className="tpd-sortable" onClick={() => toggleSort("rating", key)} title="Click to sort" >
                                                        <span className="tpd-th-inner">
                                                            {label}
                                                            <span className={`tpd-sort-icon ${tableSort.rating?.key === key ? tableSort.rating.direction === "asc" ? "asc" : "desc" : ""}`} > <i className="up" /> <i className="down" /> </span>
                                                        </span>
                                                    </th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {sortRows(data.ratingRows, "rating").length ? (
                                                sortRows(data.ratingRows, "rating").map((row) => (
                                                    <tr key={row.rating}>
                                                        <td style={{ textAlign: "left" }}>{row.rating}</td>

                                                        {["secretariat", "hods", "districts", "total"].map((key) => (
                                                            <td key={key} style={{ textAlign: "right" }}>
                                                                <button
                                                                    type="button"
                                                                    disabled={!row[key]}
                                                                    onClick={() =>
                                                                        openDetails(
                                                                            `${row.rating} ${{
                                                                                secretariat: "Secretariat",
                                                                                hods: "HOD",
                                                                                districts: "District",
                                                                                total: "Total",
                                                                            }[key]}`,
                                                                            key === "secretariat"
                                                                                ? row.records.filter((r) => levelId(r.level_of_entry) === "1")
                                                                                : key === "hods"
                                                                                    ? row.records.filter((r) => levelId(r.level_of_entry) === "2")
                                                                                    : key === "districts"
                                                                                        ? row.records.filter((r) => levelId(r.level_of_entry) === "3")
                                                                                        : row.records
                                                                        )
                                                                    }
                                                                    style={{
                                                                        color: row[key] ? "#2463b5" : "inherit",
                                                                        fontWeight: 600,
                                                                        cursor: row[key] ? "pointer" : "default",
                                                                    }}
                                                                >
                                                                    {row[key]}
                                                                </button>
                                                            </td>
                                                        ))}
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan={5} style={{ ...cellStyle, textAlign: "center" }}>
                                                        No matching records
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                        <tfoot>
                                            <tr style={{ position: "sticky", bottom: 0, background: "#eaf1fb", fontWeight: 800 }}>
                                                <td style={{ textAlign: "left" }}>Total</td>
                                                {["secretariat", "hods", "districts", "total"].map((key) => (
                                                    <td key={key} style={{ textAlign: "right" }}>
                                                        {sortRows(data.ratingRows, "rating").reduce(
                                                            (sum, row) => sum + Number(row[key] || 0), 0
                                                        )}
                                                    </td>
                                                ))}
                                            </tr>
                                        </tfoot>
                                    </table>
                                </div>

                            </div>

                        </Col>


                    </Row>



                    <Row className="g-1 mb-3">


                        {/* 4. DEPARTMENT-WISE EVALUATION */}
                        <Col xs={12} lg={6}>

                            <div className="tpd-panel">
                                <div className="tpd-panel-title tpd-title-flex">
                                    <span>Department-wise Evaluation</span>
                                    <div style={{ display: "flex", alignItems: "center", gap: "6px", marginLeft: "auto" }}>
                                        <div style={{ display: "flex", alignItems: "center", gap: "5px", width: "110px", padding: "3px 8px", border: "1px solid #ddd", borderRadius: "15px", background: "#fff" }}>
                                            <AiOutlineSearch style={{ fontSize: "13px", color: "#888", flexShrink: 0 }} />
                                            <input type="search" value={tableSearch.department} onChange={(e) => setTableSearch((p) => ({ ...p, department: e.target.value }))} style={{ width: "100%", minWidth: 0, border: "none", outline: "none", fontSize: "10px", background: "transparent" }} />
                                        </div>

                                        <button type="button" className="btn btn-sm btn-light border" style={{ padding: "1px 2px" }} title="Export to Excel" onClick={() => exportToExcel("departmentTable", "Department-Report")} > <FaFileExcel style={{ color: "#198754" }} /> </button>
                                    </div>
                                </div>



                                <div className="tpd-panel-body tpd-scroll" style={{ overflow: "auto", maxHeight: 340, padding: 0, position: "relative" }} >
                                    <table className="tpd-table" id="departmentTable" style={{ width: "100%", minWidth: "max-content", borderCollapse: "separate", borderSpacing: 0 }} >
                                        <thead>
                                            <tr>
                                                {[
                                                    ["sno", "S.No"],
                                                    ["deptcode", "Department"],
                                                    ["submitted", "Submitted"],
                                                    ["evaluated", "Evaluated"],
                                                    ["pendingEvaluation", "Pending Evaluation"],
                                                    ...ratings.map((rating) => [rating, rating]),
                                                ].map(([key, label]) => (
                                                    <th
                                                        key={key}
                                                        className="tpd-sortable"
                                                        onClick={() => toggleSort("department", key)}
                                                        title="Click to sort"

                                                    >
                                                        <span className="tpd-th-inner">
                                                            {label}
                                                            <span
                                                                className={`tpd-sort-icon ${tableSort.department?.key === key
                                                                    ? tableSort.department.direction === "asc" ? "asc" : "desc"
                                                                    : ""
                                                                    }`}
                                                            >
                                                                <i className="up" />
                                                                <i className="down" />
                                                            </span>
                                                        </span>
                                                    </th>
                                                ))}
                                            </tr>
                                        </thead>

                                        <tbody>
                                            {sortRows(data.departmentRows, "department").length ? (
                                                sortRows(data.departmentRows, "department").map((row, i) => (
                                                    <tr key={row.departmentId}>
                                                        <td>{i + 1}</td>
                                                        <td style={{ textAlign: "left" }}>{row.deptcode}</td>

                                                        {["submitted", "evaluated", "pendingEvaluation"].map((key) => (
                                                            <td key={key} style={{ textAlign: "right" }}>
                                                                <button
                                                                    type="button"
                                                                    disabled={!row[key]}
                                                                    onClick={() =>
                                                                        openDetails(
                                                                            `${row.department} ${key}`,
                                                                            key === "submitted" ? row.records :
                                                                                key === "evaluated" ? row.records.filter(isEvaluated) :
                                                                                    row.records.filter((record) => !isEvaluated(record))
                                                                        )
                                                                    }
                                                                    style={{
                                                                        border: 0,
                                                                        color: row[key] ? "#2463b5" : "inherit",
                                                                        fontWeight: 600,
                                                                        cursor: row[key] ? "pointer" : "default",
                                                                    }}
                                                                >
                                                                    {row[key]}
                                                                </button>
                                                            </td>
                                                        ))}

                                                        {ratings.map((rating) => (
                                                            <td key={rating} style={{ textAlign: "right" }}>
                                                                <button
                                                                    type="button"
                                                                    disabled={!row[rating]}
                                                                    onClick={() =>
                                                                        openDetails(
                                                                            `${row.department} — ${rating}`,
                                                                            row.records.filter((r) => isEvaluated(r) && gradeMatches(r, rating))
                                                                        )
                                                                    }
                                                                    style={{
                                                                        border: 0,
                                                                        color: row[rating] ? "#2463b5" : "inherit",
                                                                        fontWeight: 600,
                                                                        cursor: row[rating] ? "pointer" : "default",
                                                                    }}
                                                                >
                                                                    {row[rating] ?? 0}
                                                                </button>
                                                            </td>
                                                        ))}
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan={5 + ratings.length} style={{ textAlign: "center" }}>
                                                        No matching records
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>

                                        <tfoot>
                                            <tr style={{ position: "sticky", bottom: 0, background: "#eaf1fb", textAlign: "right", fontWeight: 800, }}>
                                                <td colSpan={2} style={{ textAlign: "left" }} > Total </td>
                                                {["submitted", "evaluated", "pendingEvaluation", ...ratings].map((key) => (
                                                    <td key={key} style={{ textAlign: "right" }} >
                                                        {sortRows(data.departmentRows, "department").reduce(
                                                            (sum, row) => sum + Number(row[key] || 0),
                                                            0
                                                        )}
                                                    </td>
                                                ))}
                                            </tr>
                                        </tfoot>
                                    </table>
                                </div>

                            </div>

                        </Col>



                        {/* DISTRICT-WISE EVALUATION */}
                        <Col xs={12} lg={6}>
                            <div className="tpd-panel">

                                <div className="tpd-panel-title tpd-title-flex">
                                    <span>District-wise Evaluation</span>
                                    <div style={{ display: "flex", alignItems: "center", gap: "6px", marginLeft: "auto" }}>
                                        <div style={{ display: "flex", alignItems: "center", gap: "5px", width: "110px", padding: "3px 8px", border: "1px solid #ddd", borderRadius: "15px", background: "#fff" }}>
                                            <AiOutlineSearch style={{ fontSize: "13px", color: "#888", flexShrink: 0 }} />
                                            <input type="search" value={tableSearch.district} onChange={(e) => setTableSearch((p) => ({ ...p, district: e.target.value }))} style={{ width: "100%", minWidth: 0, border: "none", outline: "none", fontSize: "10px", background: "transparent" }} />
                                        </div>
                                        <button type="button" className="btn btn-sm btn-light border" style={{ padding: "1px 2px" }} title="Export to Excel" onClick={() => exportToExcel("districtTable", "District-Report")} > <FaFileExcel style={{ color: "#198754" }} /> </button>
                                    </div>
                                </div>

                                <div className="tpd-panel-body tpd-scroll" style={{ overflow: "auto", maxHeight: 340, padding: 0, }} >
                                    <table className="tpd-table" id="districtTable" style={{ width: "100%", minWidth: "max-content" }} >
                                        <thead>
                                            <tr>
                                                {[
                                                    ["sno", "S.No"],
                                                    ["district", "District"],
                                                    ["submitted", "Submitted"],
                                                    ["evaluated", "Evaluated"],
                                                    ["pendingEvaluation", "Pending Evaluation"],
                                                    ...ratings.map((rating) => [rating, rating]),
                                                ].map(([key, label]) => (
                                                    <th key={key} className="tpd-sortable" onClick={() => toggleSort("district", key)} title="Click to sort" >
                                                        <span className="tpd-th-inner">
                                                            {label}
                                                            <span
                                                                className={`tpd-sort-icon ${tableSort.district?.key === key
                                                                    ? tableSort.district.direction === "asc"
                                                                        ? "asc"
                                                                        : "desc"
                                                                    : ""
                                                                    }`}
                                                            >
                                                                <i className="up" />
                                                                <i className="down" />
                                                            </span>
                                                        </span>
                                                    </th>
                                                ))}
                                            </tr>
                                        </thead>

                                        <tbody>
                                            {sortRows(data.districtRows, "district").length ? (
                                                sortRows(data.districtRows, "district").map((row, i) => (
                                                    <tr key={row.districtId}>
                                                        <td>{i + 1}</td>
                                                        <td className="text-left">{row.districtShort}</td>

                                                        {["submitted", "evaluated", "pendingEvaluation"].map((key) => (
                                                            <td key={key} className="text-right">
                                                                <button
                                                                    type="button"
                                                                    disabled={!row[key]}
                                                                    onClick={() =>
                                                                        openDetails(
                                                                            `${row.district} ${key}`,
                                                                            key === "submitted" ? row.records :
                                                                                key === "evaluated" ? row.records.filter(isEvaluated) :
                                                                                    row.records.filter((r) => !isEvaluated(r))
                                                                        )
                                                                    }
                                                                    style={{
                                                                        border: 0,
                                                                        color: row[key] ? "#2463b5" : "inherit",
                                                                        fontWeight: 600,
                                                                        cursor: row[key] ? "pointer" : "default",
                                                                    }}
                                                                >
                                                                    {row[key]}
                                                                </button>
                                                            </td>
                                                        ))}

                                                        {ratings.map((rating) => (
                                                            <td key={rating} className="text-right">
                                                                <button
                                                                    type="button"
                                                                    disabled={!row[rating]}
                                                                    onClick={() =>
                                                                        openDetails(
                                                                            `${row.district} — ${rating}`,
                                                                            row.records.filter(
                                                                                (r) => isEvaluated(r) && gradeMatches(r, rating)
                                                                            )
                                                                        )
                                                                    }
                                                                    style={{
                                                                        border: 0,
                                                                        color: row[rating] ? "#2463b5" : "inherit",
                                                                        fontWeight: 600,
                                                                        cursor: row[rating] ? "pointer" : "default",
                                                                    }}
                                                                >
                                                                    {row[rating] ?? 0}
                                                                </button>
                                                            </td>
                                                        ))}
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan={5 + ratings.length} style={{ ...cellStyle, textAlign: "center" }}>
                                                        No matching records
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                        <tfoot>
                                            <tr style={{ position: "sticky", bottom: 0, background: "#eaf1fb", fontWeight: 800 }}>
                                                <td colSpan={2} style={{ textAlign: "left" }}>Total</td>
                                                {["submitted", "evaluated", "pendingEvaluation", ...ratings].map((key) => (
                                                    <td key={key} style={{ textAlign: "right" }}>
                                                        {sortRows(data.districtRows, "district").reduce(
                                                            (sum, row) => sum + Number(row[key] || 0), 0
                                                        )}
                                                    </td>
                                                ))}
                                            </tr>
                                        </tfoot>
                                    </table>
                                </div>
                            </div>
                        </Col>

                    </Row>
                    <Row>
                        {/* 2. EVALUATION SUMMARY */}
                        <Col xs={12} lg={6} >


                            <div className="tpd-panel">
                                <div className="tpd-panel-title tpd-title-flex">
                                    <span>Evaluation Summary</span>
                                    <div style={{ display: "flex", alignItems: "center", gap: "6px", marginLeft: "auto" }}>
                                        <div style={{ display: "flex", alignItems: "center", gap: "5px", width: "110px", padding: "3px 8px", border: "1px solid #ddd", borderRadius: "15px", background: "#fff" }}>
                                            <AiOutlineSearch style={{ fontSize: "13px", color: "#888", flexShrink: 0 }} />
                                            <input type="search" value={tableSearch.evaluation} onChange={(e) => setTableSearch((p) => ({ ...p, evaluation: e.target.value }))} style={{ width: "100%", minWidth: 0, border: "none", outline: "none", fontSize: "10px", background: "transparent" }} />
                                        </div>
                                        <button type="button" className="btn btn-sm btn-light border" style={{ padding: "1px 2px" }} title="Export to Excel" onClick={() => exportToExcel("evaluationSummaryTable", "Evaluation-Summary")}> <FaFileExcel style={{ color: "#198754" }} /> </button>
                                    </div>
                                </div>



                                <div className="tpd-panel-body tpd-scroll" style={{ overflow: "auto", maxHeight: 340, padding: 0 }}>
                                    <table className="tpd-table" id="evaluationSummaryTable" style={{ minWidth: "max-content" }}>
                                        <thead>
                                            <tr>
                                                {[
                                                    ["category", "Level of Entry"],
                                                    ["submitted", "Submitted"],
                                                    ["evaluated", "Evaluated"],
                                                    ["pendingEvaluation", "Pending Evaluation"],
                                                    ["percentage", "Evaluated %"],
                                                ].map(([key, label]) => (
                                                    <th key={key} className="tpd-sortable" onClick={() => toggleSort("evaluation", key)} title="Click to sort" >
                                                        <span className="tpd-th-inner">
                                                            {label}
                                                            <span className={`tpd-sort-icon ${tableSort.evaluation?.key === key ? tableSort.evaluation.direction === "asc" ? "asc" : "desc" : ""}`} >
                                                                <i className="up" />
                                                                <i className="down" />
                                                            </span>
                                                        </span>
                                                    </th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {sortRows(data.evaluationRows, "evaluation").length ? (
                                                sortRows(data.evaluationRows, "evaluation").map((row) => (
                                                    <tr key={row.id}>
                                                        <td style={{ textAlign: "left" }}>{row.category}</td>

                                                        {["submitted", "evaluated", "pendingEvaluation"].map((key) => (
                                                            <td key={key} style={{ textAlign: "right" }}>
                                                                <button
                                                                    type="button"
                                                                    disabled={!row[key]}
                                                                    onClick={() =>
                                                                        openDetails(
                                                                            `${row.category} ${key}`,
                                                                            key === "submitted" ? row.records :
                                                                                key === "evaluated" ? row.records.filter(isEvaluated) :
                                                                                    row.records.filter((r) => !isEvaluated(r))
                                                                        )
                                                                    }
                                                                    style={{
                                                                        border: 0,
                                                                        color: row[key] ? "#2463b5" : "inherit",
                                                                        fontWeight: 600,
                                                                        cursor: row[key] ? "pointer" : "default",
                                                                    }}
                                                                >
                                                                    {row[key]}
                                                                </button>
                                                            </td>
                                                        ))}

                                                        <td style={{ textAlign: "right" }}>{row.percentage}%</td>
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan={5} style={{ ...cellStyle, textAlign: "center" }}>
                                                        No matching records
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                        <tfoot>
                                            <tr style={{ position: "sticky", bottom: 0, background: "#eaf1fb", fontWeight: 800 }}>
                                                <td style={{ textAlign: "left" }}>Total</td>
                                                {["submitted", "evaluated", "pendingEvaluation"].map((key) => (
                                                    <td key={key} style={{ textAlign: "right" }}>
                                                        {sortRows(data.evaluationRows, "evaluation").reduce(
                                                            (sum, row) => sum + Number(row[key] || 0), 0
                                                        )}
                                                    </td>
                                                ))}
                                                <td style={{ textAlign: "right" }}>
                                                    {(() => {
                                                        const rows = sortRows(data.evaluationRows, "evaluation");
                                                        const submitted = rows.reduce((sum, row) => sum + Number(row.submitted || 0), 0);
                                                        const evaluated = rows.reduce((sum, row) => sum + Number(row.evaluated || 0), 0);
                                                        return `${submitted ? ((evaluated / submitted) * 100).toFixed(1) : "0.0"}%`;
                                                    })()}
                                                </td>
                                            </tr>
                                        </tfoot>
                                    </table>
                                </div>

                            </div>

                        </Col>

                    </Row>



                    <Row className="g-2 mt-2 align-items-stretch">
                        <div className="tpd-panel-title">Visual Overview</div>

                        {/* Department bar chart card */}
                        <Col xs={12} lg={8}>
                            <div
                                style={{
                                    height: "100%",
                                    minHeight: 340,
                                    padding: 12,
                                    background: "#fff",
                                    border: "1px solid #dfe6ef",
                                    borderRadius: 10,
                                    boxShadow: "0 2px 8px rgba(28,48,75,0.06)",
                                }}
                            >
                                <div style={{ fontSize: 12, fontWeight: 700, color: "#334760", marginBottom: 10 }}>
                                    Department-wise Rating Distribution
                                </div>

                                <DepartmentRating3DChart
                                    rows={data.departmentRows}
                                    onRatingClick={(dept, rating) =>
                                        openDetails(
                                            `${dept.department} — ${rating}`,
                                            (dept.records || []).filter(
                                                (record) =>
                                                    isEvaluated(record) &&
                                                    gradeMatches(record, rating)
                                            )
                                        )
                                    }
                                />
                            </div>
                        </Col>


                        {/* Donut charts card */}
                        <Col xs={12} lg={4}>
                            <div
                                style={{
                                    height: "100%",
                                    minHeight: 340,
                                    padding: 12,
                                    background: "#fff",
                                    border: "1px solid #dfe6ef",
                                    borderRadius: 10,
                                    boxShadow: "0 2px 8px rgba(28,48,75,0.06)",
                                }}
                            >
                                <div
                                    style={{
                                        fontSize: 12,
                                        fontWeight: 700,
                                        color: "#334760",
                                        marginBottom: 8,
                                    }}
                                >
                                    Evaluation Distribution
                                </div>

                                <Row className="g-1">
                                    {/* Rating-wise chart */}
                                    <Col
                                        xs={data.themeRows.length > 1 ? 6 : 12}
                                        style={{ padding: "0 2px" }}
                                    >
                                        <div
                                            style={{
                                                textAlign: "center",
                                                fontSize: 11,
                                                fontWeight: 600,
                                                marginBottom: 4,
                                            }}
                                        >
                                            Rating-wise
                                        </div>

                                        <RatingEvaluationChart
                                            rows={data.themeRows}
                                            onRatingClick={(slice) =>
                                                openDetails(
                                                    `${slice.theme} — ${slice.name}`,
                                                    (slice.records || []).filter(
                                                        (record) =>
                                                            isEvaluated(record) &&
                                                            gradeMatches(record, slice.name)
                                                    )
                                                )
                                            }
                                        />
                                    </Col>

                                    {/* Show only when multiple sessions exist */}
                                    {data.themeRows.length > 1 && (
                                        <Col xs={6} style={{ padding: "0 2px" }}>
                                            <div
                                                style={{
                                                    textAlign: "center",
                                                    fontSize: 11,
                                                    fontWeight: 600,
                                                    marginBottom: 4,
                                                }}
                                            >
                                                Regular Session-wise
                                            </div>

                                            <ThemeEvaluationDonut
                                                rows={data.themeRows}
                                                onThemeClick={(row) =>
                                                    openDetails(
                                                        `${row.theme} - Evaluated`,
                                                        (row.records || []).filter(isEvaluated)
                                                    )
                                                }
                                            />
                                        </Col>
                                    )}
                                </Row>
                            </div>
                        </Col>
                    </Row>

                </Container >
            )
            }

        </>
    );
}
